import * as THREE from 'three';
import type { IceCrystals } from './IceCrystals';
import { Shape, type Particles } from './Particles';
import { KIND_COLOR, type Kind } from '../game/config';

/**
 * Elemental attack recipes, graded 1…5. Each grade changes the look, not just
 * the scale: colour temperature, density, cleanliness (smoke), structure
 * (forks, spikes, secondary splashes) and a signature at grade 5.
 *
 * Built on the shared particle draw: fire erodes through noise along a
 * temperature ramp and cools into occluding smoke; liquids are dark,
 * velocity-stretched drops with a glint, plus ground splats.
 */
const _c = new THREE.Color(), _d = new THREE.Vector3(), _s = new THREE.Vector3(), _u = new THREE.Vector3();
const rnd = (a: number, b: number) => a + Math.random() * (b - a);
const TAU = Math.PI * 2;
const g = (level: number) => Math.min(5, Math.max(1, level | 0)) - 1;

/** Flame stream: dirty red sputter at I, steady orange at III, clean white-gold breath at V. */
export const FIRE = [
  { tint: 0xff4a18, n: 3, size: 0.28, spread: 0.16, smoke: 0.6, heat: 0.95, core: 0, scorch: 0 },
  { tint: 0xff5c1e, n: 4, size: 0.32, spread: 0.2, smoke: 0.5, heat: 1.05, core: 0, scorch: 0 },
  { tint: 0xff7224, n: 5, size: 0.37, spread: 0.24, smoke: 0.38, heat: 1.2, core: 0.6, scorch: 0.45 },
  { tint: 0xff8a2e, n: 6, size: 0.43, spread: 0.28, smoke: 0.26, heat: 1.38, core: 0.85, scorch: 0.6 },
  { tint: 0xffad42, n: 7, size: 0.5, spread: 0.32, smoke: 0.14, heat: 1.6, core: 1, scorch: 0.8 },
];
/** Ice: more and taller spikes, colder mist; V raises a central spire and snow. */
export const ICE = [
  { spikes: 3, ring: 0.32, height: 0.55, mist: 1, shards: 1 },
  { spikes: 5, ring: 0.4, height: 0.68, mist: 2, shards: 1 },
  { spikes: 7, ring: 0.48, height: 0.82, mist: 3, shards: 2 },
  { spikes: 9, ring: 0.55, height: 0.96, mist: 4, shards: 2 },
  { spikes: 12, ring: 0.62, height: 1.1, mist: 5, shards: 3 },
];
/** Lightning: thicker spine, wider halo, more forks; V turns violet-white and strikes from the sky. */
export const BOLT = [
  { core: 0xffd060, color: 0xffb830, width: 0.034, halo: 0.18, forks: 0 },
  { core: 0xffd060, color: 0xffc840, width: 0.044, halo: 0.24, forks: 1 },
  { core: 0xffe08a, color: 0xffd65a, width: 0.052, halo: 0.3, forks: 2 },
  { core: 0xfff0c0, color: 0xffe27a, width: 0.06, halo: 0.36, forks: 3 },
  { core: 0xffffff, color: 0xd9b2ff, width: 0.07, halo: 0.44, forks: 4 },
];
/** Venom: bigger crown and more stains, a lingering toxic cloud from III, split splashes at V. */
export const VENOM = [
  { drops: 8, splats: 3, cloud: 0, speed: 1.1, size: 0.055 },
  { drops: 11, splats: 4, cloud: 0, speed: 1.25, size: 0.06 },
  { drops: 14, splats: 5, cloud: 2, speed: 1.4, size: 0.066 },
  { drops: 17, splats: 6, cloud: 3, speed: 1.55, size: 0.072 },
  { drops: 20, splats: 7, cloud: 4, speed: 1.7, size: 0.08 },
];

export class Elements {
  /** Faceted ice crystals; without them (headless tests) ice falls back to flat spike sprites. */
  crystals: IceCrystals | null = null;

  constructor(private readonly p: Particles, private readonly count: (n: number) => number) {}

  /** One crystal leaning away from `cx, cz` by `lean`, or the sprite fallback. */
  private crystal(x: number, z: number, width: number, height: number, life: number, delay = 0, cx = x, cz = z, lean = 0, frost = 0) {
    if (!this.crystals) {
      this.p.emit({ x, y: 0.035, z, color: _c.set(0x9fd8f5), intensity: 1.3, life, size: width, shape: Shape.IceSpike, stretch: height, delay, fade: 0.55 });
      return;
    }
    const dx = x - cx, dz = z - cz, d = Math.hypot(dx, dz) || 1;
    this.crystals.add({ x, z, width, height, life, delay, leanX: dx / d * lean, leanZ: dz / d * lean, frost });
  }

  /** One fire puff with the noise-eroded look; `smoke` > 0 lets it cool into soot. */
  fire(x: number, y: number, z: number, vx: number, vy: number, vz: number, size: number, sizeEnd: number, life: number, tint: number, heat = 1.2, smoke = 0.4, delay = 0, stretch = 0.025, gravity = -1.6, drag = 0.5) {
    this.p.emit({ x, y, z, vx, vy, vz, color: _c.set(tint), intensity: heat, life, size, sizeEnd,
      gravity, drag, shape: Shape.Fire, stretch, fade: 1, body: smoke, delay });
  }

  smoke(x: number, y: number, z: number, vx: number, vy: number, vz: number, size: number, sizeEnd: number, life: number, tint: number, opacity: number, delay = 0) {
    this.p.emit({ x, y, z, vx, vy, vz, color: _c.set(tint), intensity: 1, life, size, sizeEnd,
      gravity: -0.15, drag: 1.2, shape: Shape.Smoke, fade: 1, body: opacity, delay });
  }

  /** A tick (1/24 s) of a held flame stream from the gem to the target. */
  flameStream(from: THREE.Vector3, to: THREE.Vector3, level: number, scale: number) {
    const F = FIRE[g(level)];
    const d = _d.subVectors(to, from), dist = Math.max(0.3, d.length()); d.divideScalar(dist);
    const side = _s.set(-d.z, 0, d.x), speed = dist / 0.3;
    // A cone, not a column: each puff's width follows how far along the stream it is, so the jet
    // leaves the sphere as a thin hot tongue and only fans out as it nears the enemies.
    const reach = F.size * scale, travel = 0.3;
    // Long throws spread a tick's puffs a metre apart: stretch them along their flight so neighbours
    // join into one jet, and add puffs (up to double) so a far stream stays continuous.
    const stretch = Math.min(0.042, 0.014 + dist * 0.0035), more = Math.min(2, Math.max(1, dist / 4));
    // A far throw is mostly young (white-hot) puffs crossing the board: cool it with distance so many streams
    // converging on the gate read as flames, not a web of lasers.
    const heat = F.heat / (1 + Math.max(0, dist - 4) * 0.11);
    // The jet is lobbed on a parabola so stones at the back fire over the ones in front: each puff is born on
    // the arc with its tangent velocity and a gravity of 8h/T², so it rides the same curve (no drag on the jet).
    const T = travel, arcH = THREE.MathUtils.clamp(dist * 0.17, 0.45, 1.6), grav = 8 * arcH / (T * T);
    for (let i = 0, m = Math.max(2, this.count(F.n * more)); i < m; i++) {
      const k = rnd(0, speed / 24 * 1.15), u = k / dist, sp = rnd(0.94, 1.04);
      const lat = rnd(-1, 1) * reach * 0.55 / travel, up = rnd(-0.2, 0.5) * reach * 0.25 / travel;
      const lift = 4 * arcH * u * (1 - u), vy = d.y * speed + 4 * arcH / T * (1 - 2 * u);
      this.fire(from.x + d.x * k, from.y + d.y * k + lift, from.z + d.z * k,
        d.x * speed * sp + side.x * lat, vy * sp + up, d.z * speed * sp + side.z * lat,
        scale * 0.035 + reach * 0.6 * u, reach * rnd(0.85, 1.1), Math.max(0.12, (dist - k) / speed) * rnd(1.0, 1.08),
        F.tint, heat, F.smoke, 0, stretch, grav * sp * sp, 0);
    }
    // a white-hot root at the gem from grade III; grade V burns blue at the very base
    if (F.core > 0) {
      this.fire(from.x + d.x * 0.06, from.y + d.y * 0.06, from.z + d.z * 0.06, d.x * speed * 0.35, (d.y * speed + 4 * arcH / T) * 0.35, d.z * speed * 0.35,
        0.045 * scale, 0.1 * scale, 0.1, 0xffe2a0, 1.6 * F.core, 0, 0, 0.008);
    }
    if (level >= 5) this.p.emit({ x: from.x, y: from.y, z: from.z, color: _c.set(0x7cc4ff), intensity: 2.2, life: 0.08, size: 0.06 * scale, sizeEnd: 0.1 * scale, shape: Shape.Glow, fade: 1 });
  }

  /** Flames licking up where the stream lands (every other stream tick). */
  flameSplash(at: THREE.Vector3, level: number, scale: number) {
    const F = FIRE[g(level)];
    for (let i = 0, m = Math.max(1, this.count(1 + (level >> 1))); i < m; i++) {
      const a = Math.random() * TAU, r = rnd(0, 0.2);
      this.fire(at.x + Math.cos(a) * r, Math.max(0.1, at.y - 0.25), at.z + Math.sin(a) * r, Math.cos(a) * 0.4, rnd(0.9, 1.5), Math.sin(a) * 0.4,
        0.14 * scale, (0.3 + level * 0.04) * scale, rnd(0.32, 0.45), F.tint, F.heat * 0.9, F.smoke + 0.2);
    }
    if (F.scorch > 0) this.p.emit({ x: at.x, y: 0.03, z: at.z, color: _c.set(0xff3a10), intensity: F.scorch, life: 0.16, size: 0.3 * scale, sizeEnd: (0.45 + level * 0.05) * scale, shape: Shape.Floor, fade: 1.4 });
    if (level >= 5) {
      // grade V sets the ground itself alight around the target
      const a = Math.random() * TAU, r = rnd(0.25, 0.5);
      this.fire(at.x + Math.cos(a) * r, 0.06, at.z + Math.sin(a) * r, 0, rnd(0.5, 0.9), 0, 0.12, 0.3, 0.4, 0xff8a30, 1.2, 0.35);
    }
  }

  /** Fireball in flight (meteor): turbulent shell, a trail that cools to smoke, sparks. Call at ~30 Hz. */
  fireballTrail(pos: THREE.Vector3, vel: THREE.Vector3, scale = 1, share = 1) {
    const back = _u.copy(vel).multiplyScalar(-0.06);
    for (let i = 0, m = this.count(2 * share); i < m; i++)
      this.fire(pos.x + rnd(-0.06, 0.06), pos.y + rnd(-0.06, 0.06), pos.z + rnd(-0.06, 0.06), back.x + rnd(-0.3, 0.3), back.y + rnd(-0.2, 0.3), back.z + rnd(-0.3, 0.3),
        0.26 * scale, 0.42 * scale, 0.16, 0xffa040, 1.9, 0);
    for (let i = 0, m = this.count(2 * share); i < m; i++)
      this.fire(pos.x, pos.y, pos.z, back.x * 0.5 + rnd(-0.25, 0.25), 0.3 + rnd(0, 0.3), back.z * 0.5 + rnd(-0.25, 0.25),
        0.22 * scale, 0.5 * scale, rnd(0.28, 0.38), 0xff6a22, 1.3, 0.65);
    if (Math.random() < 0.5 * share) this.smoke(pos.x, pos.y, pos.z, rnd(-0.1, 0.1), 0.25, rnd(-0.1, 0.1), 0.25 * scale, 0.7 * scale, rnd(0.7, 1), 0x2a2420, 0.45, 0.05);
  }

  /** Fireball landing: a fire dome and a rising soot column on top of the crater. */
  fireballImpact(at: THREE.Vector3, scale = 1) {
    for (let i = 0, m = Math.max(6, this.count(14)); i < m; i++) {
      const a = i / m * TAU + rnd(-0.2, 0.2), sp = rnd(1, 2.6);
      this.fire(at.x, 0.15, at.z, Math.cos(a) * sp, rnd(0.8, 2.2), Math.sin(a) * sp,
        0.25 * scale, rnd(0.55, 0.8) * scale, rnd(0.5, 0.8), i % 3 ? 0xff7a2a : 0xffb050, 1.6, 0.55);
    }
    for (let i = 0, m = Math.max(2, this.count(5)); i < m; i++)
      this.smoke(at.x + rnd(-0.3, 0.3), 0.3 + i * 0.15, at.z + rnd(-0.3, 0.3), rnd(-0.2, 0.2), rnd(0.6, 1.2), rnd(-0.2, 0.2),
        0.45 * scale, rnd(1.2, 1.7) * scale, rnd(1.6, 2.4), 0x2b2623, 0.6, 0.08 + i * 0.05);
  }

  /** Sapphire eruption: spikes, mist, delayed shatter; the spire and snow are grade V. */
  iceEruption(at: THREE.Vector3, level: number) {
    const I = ICE[g(level)];
    for (let i = 0; i < I.spikes; i++) {
      const center = i === 0, a = i / (I.spikes - 1) * TAU + level, r = center ? 0 : I.ring * (0.75 + 0.35 * ((i * 7) % 3) / 2);
      const x = at.x + Math.cos(a) * r, z = at.z + Math.sin(a) * r;
      const h = center ? I.height * (level >= 5 ? 1.9 : 1.15) : I.height * rnd(0.6, 1);
      const w = center && level >= 5 ? 0.42 : 0.17 + level * 0.012 + (i % 2) * 0.06;
      // crystals burst outward from the impact: the outer ring leans away, the centre stands tall
      this.crystal(x, z, w * 0.95, h, 0.62 + level * 0.06, r * 0.06, at.x, at.z, center ? 0 : rnd(0.25, 0.45), level >= 4 ? 0.5 : 0);
      for (let k = 0; k < I.shards; k++) {
        const ang = Math.random() * TAU;
        this.p.emit({ x, y: h * rnd(0.35, 0.7), z, vx: Math.cos(ang) * rnd(0.4, 1), vy: rnd(0.3, 1.1), vz: Math.sin(ang) * rnd(0.4, 1),
          color: _c.set(0xa8dcff), intensity: 1.8, life: rnd(0.35, 0.5), size: rnd(0.045, 0.07), sizeEnd: 0.01,
          gravity: 9, drag: 1.5, shape: Shape.Shard, stretch: 0.03, fade: 0.8, delay: 0.5 + level * 0.05 + r * 0.06 });
      }
    }
    for (let i = 0, m = Math.max(1, this.count(I.mist)); i < m; i++) {
      const a = Math.random() * TAU;
      this.smoke(at.x + Math.cos(a) * I.ring * 0.6, 0.1, at.z + Math.sin(a) * I.ring * 0.6, Math.cos(a) * 0.4, 0.1, Math.sin(a) * 0.4,
        0.3, 0.75 + level * 0.06, rnd(0.9, 1.3), 0xb8d4ea, 0.14 + level * 0.035, 0.08);
    }
    if (level >= 4) this.p.emit({ x: at.x, y: 0.05, z: at.z, color: _c.set(0xd8f0ff), intensity: 2, life: 0.45, size: 0.3, sizeEnd: I.ring * 3, shape: Shape.Ring, fade: 1.4 });
    if (level >= 5) for (let i = 0, m = this.count(16); i < m; i++) {
      const a = Math.random() * TAU, r = Math.random() * 1.1;
      this.p.emit({ x: at.x + Math.cos(a) * r, y: rnd(0.9, 1.6), z: at.z + Math.sin(a) * r, vx: rnd(-0.1, 0.1), vy: rnd(-0.1, 0.2), vz: rnd(-0.1, 0.1),
        color: _c.set(0xeaf6ff), intensity: 1.8, life: rnd(1, 1.5), size: rnd(0.025, 0.04), sizeEnd: 0.01, gravity: 0.5, drag: 1.5, shape: Shape.Glow, fade: 0.8, delay: rnd(0.05, 0.3) });
    }
  }

  /**
   * Lightning link: spine, halo and forks graded by level; grade V is violet-white.
   * Emissions stay bounded: ≤ 24 spine + 8 halo + 4×3 forks + 1 glint.
   */
  bolt(a: THREE.Vector3, b: THREE.Vector3, level: number, intensity = 2.6) {
    const B = BOLT[g(level)], col = _c.set(B.color);
    const d = _d.subVectors(b, a), len = d.length(), n = Math.min(24, Math.max(3, Math.ceil(len * 7)));
    const side = _s.set(-d.z, 0, d.x).normalize(), forkAt = new Set<number>();
    for (let f = 0; f < B.forks; f++) forkAt.add(1 + ((Math.random() * (n - 2)) | 0));
    const jag = 0.06 + level * 0.008;
    // long bolts dim with length and brighten toward the strike, so crossfire reads as hits, not a lattice
    const far = 1 / (1 + Math.max(0, len - 4) * 0.08);
    let jx = 0, jy = 0, px = a.x, py = a.y, pz = a.z, hx = a.x, hy = a.y, hz = a.z;
    for (let i = 1; i <= n; i++) {
      const t = i / n, env = Math.sin(t * Math.PI);
      jx += rnd(-jag, jag); jy += rnd(-jag, jag); jx *= 0.8; jy *= 0.8;
      const nx = a.x + d.x * t + side.x * jx * env * 2, ny = a.y + d.y * t + jy * env * 2, nz = a.z + d.z * t + side.z * jx * env * 2;
      this.p.emit({ x: (px + nx) * 0.5, y: (py + ny) * 0.5, z: (pz + nz) * 0.5, vx: nx - px, vy: ny - py, vz: nz - pz,
        color: _c.set(B.core), intensity: intensity * far * (0.55 + 0.45 * t), life: 0.16, size: B.width, sizeEnd: B.width * 0.4, shape: Shape.Arc, stretch: 1.12, fade: 1.2 });
      if (i % 3 === 0 || i === n) {
        this.p.emit({ x: (hx + nx) * 0.5, y: (hy + ny) * 0.5, z: (hz + nz) * 0.5, vx: nx - hx, vy: ny - hy, vz: nz - hz,
          color: col.set(B.color), intensity: intensity * B.halo * far * (0.4 + 0.6 * t), life: 0.14, size: (0.16 + level * 0.03) * (0.7 + 0.3 * far), sizeEnd: 0.1, shape: Shape.Arc, stretch: 1.04, fade: 1.4 });
        hx = nx; hy = ny; hz = nz;
      }
      if (forkAt.has(i)) {
        let fx = nx, fy = ny, fz = nz;
        const sgn = Math.random() < 0.5 ? -1 : 1, step = len / n * (0.7 + level * 0.06);
        for (let k = 0; k < 3; k++) {
          const ex = fx + (d.x / len * 0.6 + side.x * sgn * 0.8) * step + rnd(-0.03, 0.03);
          const ey = fy + rnd(-0.05, 0.08), ez = fz + (d.z / len * 0.6 + side.z * sgn * 0.8) * step + rnd(-0.03, 0.03);
          this.p.emit({ x: (fx + ex) * 0.5, y: (fy + ey) * 0.5, z: (fz + ez) * 0.5, vx: ex - fx, vy: ey - fy, vz: ez - fz,
            color: col.set(B.color), intensity: intensity * (0.75 - k * 0.18), life: 0.1, size: B.width * (0.7 - k * 0.15), sizeEnd: 0.01,
            shape: Shape.Arc, stretch: 1.1, fade: 1.4 });
          fx = ex; fy = ey; fz = ez;
        }
      }
      px = nx; py = ny; pz = nz;
    }
    this.p.emit({ x: b.x, y: b.y, z: b.z, vx: 0, vy: 0, vz: 0, color: col.set(B.color), intensity: intensity * (0.6 + level * 0.12), life: 0.08,
      size: 0.08 + level * 0.02, sizeEnd: 0.3 + level * 0.05, shape: Shape.Flare, fade: 1.2 });
  }

  /** Grade V signature: a bolt from the sky onto the first target. */
  skyStrike(at: THREE.Vector3) {
    this.bolt(_u.set(at.x - 0.4, 5, at.z - 0.3), at, 5, 3.4);
    this.p.emit({ x: at.x, y: 0.04, z: at.z, color: _c.set(0xe6c8ff), intensity: 2.4, life: 0.3, size: 0.4, sizeEnd: 1.4, shape: Shape.Sun, fade: 1.4 });
  }

  /** Spore splash: a crown of dark droplets, stains where they land, toxic cloud from III, split splashes at V. */
  venomSplash(at: THREE.Vector3, level: number, radius: number, scale: number) {
    const V = VENOM[g(level)];
    const splash = (cx: number, cz: number, k: number, delay: number) => {
      for (let i = 0, m = Math.max(4, this.count(Math.round(V.drops * k))); i < m; i++) {
        const a = i / m * TAU + rnd(-0.25, 0.25), sp = V.speed * rnd(0.7, 1.2) * Math.sqrt(k);
        this.p.emit({ x: cx, y: 0.12, z: cz, vx: Math.cos(a) * sp, vy: rnd(1.3, 2.3) * Math.sqrt(k), vz: Math.sin(a) * sp,
          color: _c.set(0x54c845), intensity: 1.1, life: rnd(0.4, 0.6), size: V.size * scale * rnd(0.8, 1.2), sizeEnd: V.size * scale * 0.6,
          gravity: 8, drag: 0.4, shape: Shape.Drop, stretch: 0.06, fade: 1, body: 0.9, delay });
      }
      for (let i = 0, m = Math.max(2, this.count(Math.round(V.splats * k))); i < m; i++) {
        const a = Math.random() * TAU, r = Math.sqrt(Math.random()) * radius * 1.15 * Math.sqrt(k);
        this.p.emit({ x: cx + Math.cos(a) * r, y: 0.05, z: cz + Math.sin(a) * r, color: _c.set(0x2f9a2c), intensity: 1, life: rnd(1.6, 2.6),
          size: radius * rnd(0.22, 0.42) * Math.sqrt(k), shape: Shape.Splat, fade: 1, body: 0.8, delay: delay + rnd(0.18, 0.32) });
      }
    };
    splash(at.x, at.z, 1, 0);
    if (level >= 5) for (let s = 0; s < 2; s++) {
      const a = Math.random() * TAU;
      splash(at.x + Math.cos(a) * radius * 0.9, at.z + Math.sin(a) * radius * 0.9, 0.45, 0.22 + s * 0.08);
    }
    for (let i = 0, m = this.count(V.cloud); i < m; i++) {
      const a = Math.random() * TAU, r = Math.random() * radius * 0.6;
      this.smoke(at.x + Math.cos(a) * r, 0.15, at.z + Math.sin(a) * r, Math.cos(a) * 0.2, rnd(0.2, 0.4), Math.sin(a) * 0.2,
        0.4, 1 + level * 0.08, rnd(1.4, 2), 0x5f9a45, 0.2 + level * 0.03, 0.1 + i * 0.06);
    }
  }

  /** Spore in flight: a falling droplet (call at ~30 Hz). */
  venomDrip(pos: THREE.Vector3, vel: THREE.Vector3, level: number) {
    this.p.emit({ x: pos.x, y: pos.y - 0.04, z: pos.z, vx: vel.x * 0.12, vy: -0.3, vz: vel.z * 0.12,
      color: _c.set(0x54c845), intensity: 1.1, life: 0.5, size: 0.03 + level * 0.006, sizeEnd: 0.022,
      gravity: 7, shape: Shape.Drop, stretch: 0.06, fade: 1, body: 0.9 });
  }

  /* ---------------- merge & summon ---------------- */

  /** Thin jagged energy arc in any colour (merge tether, summon crackle). Bounded to 12 segments. */
  arc(a: THREE.Vector3, b: THREE.Vector3, color: number, width = 0.03, intensity = 2.4, life = 0.09, delay = 0) {
    const d = _d.subVectors(b, a), len = d.length(), n = Math.min(12, Math.max(3, Math.ceil(len * 8)));
    const side = _s.set(-d.z, 0, d.x).normalize();
    let jx = 0, jy = 0, px = a.x, py = a.y, pz = a.z;
    for (let i = 1; i <= n; i++) {
      const t = i / n, env = Math.sin(t * Math.PI);
      jx += rnd(-0.05, 0.05); jy += rnd(-0.05, 0.05); jx *= 0.75; jy *= 0.75;
      const nx = a.x + d.x * t + side.x * jx * env * 2, ny = a.y + d.y * t + jy * env * 2 + env * 0.06, nz = a.z + d.z * t + side.z * jx * env * 2;
      this.p.emit({ x: (px + nx) * 0.5, y: (py + ny) * 0.5, z: (pz + nz) * 0.5, vx: nx - px, vy: ny - py, vz: nz - pz,
        color: _c.set(color), intensity, life, size: width, sizeEnd: width * 0.4, shape: Shape.Arc, stretch: 1.12, fade: 1.2, delay });
      px = nx; py = ny; pz = nz;
    }
  }

  /** One charge step (every 40 ms) while two stones fuse: the element gathers toward the heart. */
  mergeCharge(kind: Kind, center: THREE.Vector3, level: number, duration: number) {
    const col = KIND_COLOR[kind];
    for (let i = 0, m = this.count(2 + level); i < m; i++) {
      const a = Math.random() * TAU, r = rnd(0.45, 0.8), h = rnd(-0.15, 0.35);
      const x = center.x + Math.cos(a) * r, y = center.y + h, z = center.z + Math.sin(a) * r, life = rnd(0.16, 0.24);
      // tangential swirl + inward pull, timed to arrive as the particle dies
      const vx = (center.x - x) / life - Math.sin(a) * 1.2, vy = (center.y - y) / life, vz = (center.z - z) / life + Math.cos(a) * 1.2;
      if (kind === 'r') this.fire(x, y, z, vx, vy, vz, 0.12, 0.05, life, FIRE[g(level)].tint, 1.2, 0);
      else if (kind === 'g') this.p.emit({ x, y, z, vx, vy, vz, color: _c.set(0x54c845), intensity: 1.1, life, size: 0.05, sizeEnd: 0.03, shape: Shape.Drop, stretch: 0.03, fade: 1, body: 0.9 });
      else if (kind === 'v') this.p.emit({ x, y, z, vx, vy, vz, color: _c.set(col), intensity: 1.4, life, size: 0.06, sizeEnd: 0.02, shape: Shape.Drop, stretch: 0.02, fade: 0.8, body: 0.6 });
      else this.p.emit({ x, y, z, vx, vy, vz, color: _c.set(kind === 'b' ? 0xbfe6ff : col), intensity: 2.4, life, size: kind === 'b' ? 0.05 : 0.03, sizeEnd: 0.01,
        shape: kind === 'b' ? Shape.Shard : Shape.Spark, stretch: 0.03, fade: 1 });
    }
    if (kind === 'y' && Math.random() < 0.6) {
      const a = Math.random() * TAU;
      this.arc(_u.set(center.x + Math.cos(a) * 0.55, center.y + rnd(-0.1, 0.25), center.z + Math.sin(a) * 0.55), center, col, 0.025, 2.2, 0.06);
    }
    void duration;
  }

  /**
   * The new grade is born: each element releases in its own shape, scaled by grade.
   * `center` is the gem, `floorY` the well floor under it.
   */
  mergeBurst(kind: Kind, center: THREE.Vector3, floorY: number, level: number, scale = 1) {
    const k = (level - 1) / 4, col = KIND_COLOR[kind];
    switch (kind) {
      case 'r': {
        const tint = FIRE[g(level)].tint;
        // a ring of flame spins up off the altar and a column climbs from the well
        for (let i = 0, m = this.count(Math.round((10 + level * 4) * scale)); i < m; i++) {
          const a = i / m * TAU, r = 0.42 * scale;
          this.fire(center.x + Math.cos(a) * r, floorY + 0.08, center.z + Math.sin(a) * r,
            -Math.sin(a) * 1.6 + Math.cos(a) * 0.5, rnd(1.2, 2.2) + k, Math.cos(a) * 1.6 + Math.sin(a) * 0.5,
            0.14 * scale, (0.3 + k * 0.15) * scale, rnd(0.45, 0.7), tint, 1.0, 0.35, i * 0.004);
        }
        for (let i = 0, m = this.count(Math.round((4 + level) * scale)); i < m; i++)
          this.fire(center.x + rnd(-0.08, 0.08), floorY + 0.1, center.z + rnd(-0.08, 0.08), rnd(-0.2, 0.2), rnd(2.4, 3.6) + k, rnd(-0.2, 0.2),
            0.2 * scale, (0.45 + k * 0.2) * scale, rnd(0.5, 0.75), i ? tint : 0xffc060, 1.15, 0.3, i * 0.02);
        break;
      }
      case 'b': {
        // a crown of ice bursts from the well rim, then snow glitters down
        const n = Math.round((4 + level * 2) * scale);
        for (let i = 0; i < n; i++) {
          const a = i / n * TAU, r = (0.38 + k * 0.12) * scale;
          this.crystal(center.x + Math.cos(a) * r, center.z + Math.sin(a) * r, (0.08 + k * 0.04) * scale, (0.35 + k * 0.4) * scale * rnd(0.8, 1.15),
            0.8 + k * 0.3, i * 0.012, center.x, center.z, 0.35, 0.3);
        }
        for (let i = 0, m = this.count(Math.round((10 + level * 4) * scale)); i < m; i++) {
          const a = Math.random() * TAU, sp = rnd(0.6, 1.6);
          this.p.emit({ x: center.x, y: center.y, z: center.z, vx: Math.cos(a) * sp, vy: rnd(0.8, 2), vz: Math.sin(a) * sp, color: _c.set(0xeaf6ff), intensity: 2,
            life: rnd(0.8, 1.3), size: rnd(0.025, 0.045), sizeEnd: 0.01, gravity: 1.2, drag: 2.2, shape: Shape.Glow, fade: 0.8 });
        }
        this.p.emit({ x: center.x, y: floorY + 0.03, z: center.z, color: _c.set(0x74c3ff), intensity: 1.6, life: 0.9, size: 0.4 * scale, sizeEnd: (1.2 + k) * scale, shape: Shape.Frost, fade: 1.1 });
        this.smoke(center.x, floorY + 0.12, center.z, 0, 0.2, 0, 0.4 * scale, (1 + k * 0.6) * scale, 1.1, 0xc4dcef, 0.18 + k * 0.1);
        break;
      }
      case 'y': {
        // radial discharges to points around the altar, a sun mark under the stone
        const n = Math.round((3 + level) * scale);
        for (let i = 0; i < n; i++) {
          const a = i / n * TAU + rnd(-0.2, 0.2), r = (0.75 + k * 0.5) * scale;
          const tx = center.x + Math.cos(a) * r, tz = center.z + Math.sin(a) * r, bc = level >= 5 ? BOLT[4].color : col;
          // two strikes along fresh paths, so the discharge flickers instead of blinking once
          this.arc(center, _u.set(tx, floorY + 0.05, tz), bc, 0.03 + k * 0.02, 2.6, 0.16);
          this.arc(center, _u.set(tx, floorY + 0.05, tz), bc, 0.025 + k * 0.015, 2.2, 0.14, 0.12);
          this.p.emit({ x: _u.x, y: _u.y, z: _u.z, color: _c.set(0xfff0b0), intensity: 2.2, life: 0.12, size: 0.06, sizeEnd: 0.25, shape: Shape.Flare, fade: 1.2 });
        }
        this.p.emit({ x: center.x, y: floorY + 0.03, z: center.z, color: _c.set(col), intensity: 2, life: 0.45, size: 0.4 * scale, sizeEnd: (1.1 + k * 0.6) * scale, shape: Shape.Sun, fade: 1.3 });
        break;
      }
      case 'g': {
        // the stone bursts like a ripe pod: a crown of drops, stains across the altar, a bubbling haze
        for (let i = 0, m = this.count(Math.round((10 + level * 3) * scale)); i < m; i++) {
          const a = i / m * TAU + rnd(-0.2, 0.2), sp = rnd(1, 1.8) * (0.8 + k * 0.4);
          this.p.emit({ x: center.x, y: center.y, z: center.z, vx: Math.cos(a) * sp, vy: rnd(1.4, 2.4), vz: Math.sin(a) * sp,
            color: _c.set(0x54c845), intensity: 1.2, life: rnd(0.45, 0.65), size: rnd(0.05, 0.08) * scale, sizeEnd: 0.03, gravity: 8, drag: 0.4,
            shape: Shape.Drop, stretch: 0.06, fade: 1, body: 0.9 });
        }
        for (let i = 0, m = this.count(Math.round((3 + level) * scale)); i < m; i++) {
          const a = Math.random() * TAU, r = rnd(0.2, 0.75) * scale;
          this.p.emit({ x: center.x + Math.cos(a) * r, y: floorY + 0.02, z: center.z + Math.sin(a) * r, color: _c.set(0x2f9a2c), intensity: 1,
            life: rnd(1.4, 2.2), size: rnd(0.12, 0.2) * scale, shape: Shape.Splat, fade: 1, body: 0.8, delay: rnd(0.2, 0.35) });
        }
        this.smoke(center.x, center.y, center.z, 0, 0.3, 0, 0.3 * scale, (0.9 + k * 0.5) * scale, 1.3, 0x5f9a45, 0.2 + k * 0.08);
        break;
      }
      case 'a': {
        // a starburst of arcane motes flung out in a spiral, then drifting back as glitter
        for (let i = 0, m = this.count(Math.round((12 + level * 4) * scale)); i < m; i++) {
          const a = i / m * TAU, sp = rnd(1.4, 2.6) * (0.8 + k * 0.4);
          this.p.emit({ x: center.x, y: center.y, z: center.z, vx: (Math.cos(a) - Math.sin(a) * 0.6) * sp, vy: rnd(-0.3, 1.2), vz: (Math.sin(a) + Math.cos(a) * 0.6) * sp,
            color: _c.set(i % 3 ? col : 0xf0e0ff), intensity: 2.4, life: rnd(0.35, 0.55), size: 0.035, sizeEnd: 0.01, drag: 2.5, shape: Shape.Spark, stretch: 0.05, fade: 1 });
        }
        this.p.emit({ x: center.x, y: floorY + 0.03, z: center.z, color: _c.set(col), intensity: 1.8, life: 0.4, size: 0.3 * scale, sizeEnd: (1 + k * 0.5) * scale, shape: Shape.Ring, fade: 1.3 });
        break;
      }
      case 'o': {
        // the onyx implodes: motes rush inward, then a dark-pink ring snaps outward
        for (let i = 0, m = this.count(Math.round((12 + level * 3) * scale)); i < m; i++) {
          const a = i / m * TAU, r = rnd(0.6, 1.1), life = 0.3;
          this.p.emit({ x: center.x + Math.cos(a) * r, y: center.y + rnd(-0.2, 0.3), z: center.z + Math.sin(a) * r, vx: -Math.cos(a) * r / life - Math.sin(a) * 1.5, vy: 0, vz: -Math.sin(a) * r / life + Math.cos(a) * 1.5,
            color: _c.set(col), intensity: 2.2, life, size: 0.035, sizeEnd: 0.008, shape: Shape.Spark, stretch: 0.04, fade: 1 });
        }
        this.p.emit({ x: center.x, y: floorY + 0.03, z: center.z, color: _c.set(col), intensity: 2, life: 0.45, size: 0.2 * scale, sizeEnd: (1.3 + k * 0.6) * scale, shape: Shape.Ring, fade: 1.3, delay: 0.28 });
        break;
      }
      case 'w': {
        // the prism splits light: a ring of rainbow glints
        for (let i = 0, m = this.count(Math.round((12 + level * 3) * scale)); i < m; i++) {
          const a = i / m * TAU, sp = rnd(1, 2);
          this.p.emit({ x: center.x, y: center.y, z: center.z, vx: Math.cos(a) * sp, vy: rnd(0.2, 1.2), vz: Math.sin(a) * sp,
            color: _c.setHSL(i / m, 0.9, 0.65), intensity: 2.2, life: rnd(0.4, 0.6), size: 0.03, sizeEnd: 0.01, drag: 2, shape: Shape.Spark, stretch: 0.04, fade: 1 });
        }
        break;
      }
      case 'v': {
        // A ring of blood drawn into the beating heart rather than a fire burst.
        for (let i = 0, n = this.count(8 + level * 2); i < n; i++) {
          const a = i / n * TAU, r = (0.6 + k * 0.25) * scale;
          this.bloodStrike(center, _u.set(center.x + Math.cos(a) * r, center.y + Math.sin(a * 2) * 0.1, center.z + Math.sin(a) * r), level);
        }
        break;
      }
      case 't':
        this.timeLock(center, scale * (1 + k * 0.45), 0.8 + k * 0.5, floorY + 0.035);
        break;
    }
  }

  /** A single blood bite with a silky return tether: no missile, splash or chain. */
  bloodStrike(from: THREE.Vector3, to: THREE.Vector3, level: number) {
    const vx = from.x - to.x, vy = from.y - to.y, vz = from.z - to.z;
    this.p.emit({ x: (from.x + to.x) * 0.5, y: (from.y + to.y) * 0.5, z: (from.z + to.z) * 0.5,
      vx, vy, vz, color: _c.set(KIND_COLOR.v), intensity: 1.8, life: 0.38, size: 0.13 + level * 0.012, sizeEnd: 0.035,
      shape: Shape.BloodThread, stretch: 1, fade: 0.8 });
    // Two close fang marks at the chest, then small crimson drops returning along the tether.
    for (const side of [-1, 1]) this.p.emit({ x: to.x + side * 0.07, y: to.y, z: to.z, vx: side * 0.45, vy: -1.2,
      color: _c.set(0xff7498), intensity: 2.2, life: 0.16, size: 0.038, sizeEnd: 0.008, shape: Shape.Spark, stretch: 0.13, fade: 1 });
    for (let i = 0, n = this.count(3 + (level >> 1)); i < n; i++) {
      const flight = 0.3;
      this.p.emit({ x: to.x, y: to.y + (i % 2 ? 0.03 : -0.03), z: to.z, vx: vx / flight, vy: vy / flight, vz: vz / flight,
        color: _c.set(KIND_COLOR.v), intensity: 1.6, life: flight, size: 0.055, sizeEnd: 0.02, shape: Shape.Drop, stretch: 0.008, body: 0.65, fade: 0.6, delay: i * 0.035 });
    }
  }

  /** Time is sealed in a brass dial; the armour gets a golden rim instead of an ice crust. */
  timeLock(at: THREE.Vector3, scale: number, duration: number, floorY = 0.04) {
    this.p.emit({ x: at.x, y: floorY, z: at.z, color: _c.set(KIND_COLOR.t), intensity: 1.8, life: duration,
      size: 1.15 * scale, sizeEnd: 1.15 * scale, shape: Shape.Clock, fade: 0.1 });
    for (let i = 0, n = this.count(4); i < n; i++) {
      const a = i / n * TAU, r = scale * 0.32;
      this.p.emit({ x: at.x + Math.cos(a) * r, y: at.y - scale * 0.3, z: at.z + Math.sin(a) * r,
        color: _c.set(0xffe0a1), intensity: 1.5, life: duration, size: 0.045, sizeEnd: 0.015, shape: Shape.Glow, fade: 0.25 });
    }
  }

  timeResume(at: THREE.Vector3, scale: number) {
    this.p.emit({ x: at.x, y: 0.04, z: at.z, color: _c.set(KIND_COLOR.t), intensity: 0.9, life: 0.18,
      size: 1.0 * scale, sizeEnd: 1.4 * scale, shape: Shape.Ring, fade: 1.5 });
  }

  /** Onyx: a dark-pink thread of motes pulled from the orb down into the opening well. */
  gravityCast(from: THREE.Vector3, at: THREE.Vector3, level: number) {
    const col = KIND_COLOR.o, life = 0.28;
    for (let i = 0, n = this.count(6 + level); i < n; i++) {
      const u = i / n;
      this.p.emit({ x: from.x, y: from.y, z: from.z, vx: (at.x - from.x) / life, vy: (at.y - from.y) / life + 1.2 * (1 - 2 * u), vz: (at.z - from.z) / life,
        color: _c.set(i % 3 ? col : 0xffd6ec), intensity: 2.2, life, size: 0.035, sizeEnd: 0.012, shape: Shape.Spark, stretch: 0.03, fade: 0.6, delay: u * 0.12 });
    }
    this.p.emit({ x: at.x, y: 0.05, z: at.z, color: _c.set(col), intensity: 1.6, life: 0.35, size: 1.4, sizeEnd: 0.2, shape: Shape.Ring, fade: 0.8, delay: 0.2 });
  }

  /** Inside a live well: motes and dust spiral inward and vanish at the centre (call ~14 Hz). */
  gravitySwirl(at: THREE.Vector3, radius: number, level: number) {
    for (let i = 0, n = this.count(2 + (level >> 1)); i < n; i++) {
      const a = Math.random() * TAU, r = radius * rnd(0.6, 1), life = rnd(0.45, 0.6);
      const x = at.x + Math.cos(a) * r, z = at.z + Math.sin(a) * r;
      this.p.emit({ x, y: rnd(0.05, 0.4), z, vx: (at.x - x) / life - Math.sin(a) * r * 2.4, vy: -0.1, vz: (at.z - z) / life + Math.cos(a) * r * 2.4,
        color: _c.set(i % 2 ? KIND_COLOR.o : 0xffc4e4), intensity: 1.8, life, size: 0.03, sizeEnd: 0.005, drag: 1.2, shape: Shape.Spark, stretch: 0.03, fade: 0.8 });
    }
  }

  /** An arcane missile: a bright violet head with a short tail racing to the target in `flight` seconds. */
  arcaneMissile(from: THREE.Vector3, to: THREE.Vector3, level: number, flight: number) { this.missile(from, to, level, flight, KIND_COLOR.a, 0xf2e6ff); }

  /** A missile's head and tail racing to the target (arcane, obsidian, the storm eye share it in their colours). */
  missile(from: THREE.Vector3, to: THREE.Vector3, level: number, flight: number, col: number, head: number) {
    const vx = (to.x - from.x) / flight, vy = (to.y - from.y) / flight, vz = (to.z - from.z) / flight;
    this.p.emit({ x: from.x, y: from.y, z: from.z, vx, vy, vz, color: _c.set(head), intensity: 2.6, life: flight, size: 0.07 + level * 0.008, sizeEnd: 0.06, shape: Shape.Glow, fade: 0.3 });
    this.p.emit({ x: from.x, y: from.y, z: from.z, vx, vy, vz, color: _c.set(col), intensity: 2.2, life: flight, size: 0.05, sizeEnd: 0.04, shape: Shape.Spark, stretch: 0.012, fade: 0.3 });
    for (let i = 0, n = this.count(2 + (level >> 1)); i < n; i++) {
      const d = flight * (i + 1) / (n + 1);
      this.p.emit({ x: from.x, y: from.y, z: from.z, vx, vy, vz, color: _c.set(col), intensity: 1.6, life: 0.12, size: 0.03, sizeEnd: 0, drag: 6, shape: Shape.Glow, fade: 1, delay: d });
    }
  }

  /** The summoned stone in flight: its element trails behind it (call at ~60 Hz). */
  summonTrail(kind: Kind, pos: THREE.Vector3, vel: THREE.Vector3) {
    const bx = -vel.x * 0.05, by = -vel.y * 0.05, bz = -vel.z * 0.05;
    switch (kind) {
      case 'r': this.fire(pos.x, pos.y, pos.z, bx + rnd(-0.2, 0.2), by + 0.3, bz + rnd(-0.2, 0.2), 0.12, 0.26, 0.28, FIRE[1].tint, 1.4, 0.4); break;
      case 'b': this.p.emit({ x: pos.x, y: pos.y, z: pos.z, vx: bx + rnd(-0.4, 0.4), vy: by + rnd(-0.2, 0.4), vz: bz + rnd(-0.4, 0.4), color: _c.set(0xcfeeff), intensity: 2.2,
        life: 0.4, size: 0.05, sizeEnd: 0.01, gravity: 3, drag: 1.5, shape: Shape.Shard, stretch: 0.03, fade: 0.8 }); break;
      case 'y': if (Math.random() < 0.5) this.arc(pos, _u.set(pos.x + rnd(-0.25, 0.25), pos.y + rnd(-0.2, 0.2), pos.z + rnd(-0.25, 0.25)), KIND_COLOR.y, 0.02, 2.4, 0.06); break;
      case 'g': if (Math.random() < 0.6) this.p.emit({ x: pos.x, y: pos.y - 0.03, z: pos.z, vx: bx * 0.5, vy: -0.2, vz: bz * 0.5, color: _c.set(0x54c845), intensity: 1.1,
        life: 0.45, size: 0.04, sizeEnd: 0.025, gravity: 7, shape: Shape.Drop, stretch: 0.06, fade: 1, body: 0.9 }); break;
      case 'a': this.p.emit({ x: pos.x, y: pos.y, z: pos.z, vx: bx + rnd(-0.3, 0.3), vy: by + rnd(-0.2, 0.3), vz: bz + rnd(-0.3, 0.3), color: _c.set(KIND_COLOR.a), intensity: 2.2,
        life: 0.3, size: 0.035, sizeEnd: 0, drag: 2, shape: Shape.Spark, stretch: 0.03, fade: 1 }); break;
      case 'o': this.p.emit({ x: pos.x + rnd(-0.2, 0.2), y: pos.y + rnd(-0.15, 0.15), z: pos.z + rnd(-0.2, 0.2), vx: bx, vy: by, vz: bz, color: _c.set(KIND_COLOR.o), intensity: 2,
        life: 0.25, size: 0.03, sizeEnd: 0, drag: 2, shape: Shape.Spark, stretch: 0.03, fade: 1 }); break;
      case 'w': this.p.emit({ x: pos.x, y: pos.y, z: pos.z, vx: bx + rnd(-0.3, 0.3), vy: by + rnd(-0.2, 0.3), vz: bz + rnd(-0.3, 0.3), color: _c.setHSL(Math.random(), 0.9, 0.7), intensity: 2,
        life: 0.35, size: 0.03, sizeEnd: 0, drag: 2, shape: Shape.Spark, stretch: 0.03, fade: 1 }); break;
      case 'v': this.p.emit({ x: pos.x, y: pos.y, z: pos.z, vx: bx, vy: by, vz: bz, color: _c.set(KIND_COLOR.v), intensity: 1.5,
        life: 0.35, size: 0.055, sizeEnd: 0.012, shape: Shape.Drop, stretch: 0.03, body: 0.6, fade: 1 }); break;
      case 't': this.p.emit({ x: pos.x, y: pos.y, z: pos.z, vx: bx, vy: by + 0.15, vz: bz, color: _c.set(0xffdf9a), intensity: 1.5,
        life: 0.3, size: 0.03, sizeEnd: 0.008, shape: Shape.Glow, fade: 1 }); break;
      case 'k': case 's': this.p.emit({ x: pos.x, y: pos.y, z: pos.z, vx: bx + rnd(-0.3, 0.3), vy: by + rnd(-0.2, 0.3), vz: bz + rnd(-0.3, 0.3), color: _c.set(KIND_COLOR[kind]), intensity: 2.2,
        life: 0.3, size: 0.035, sizeEnd: 0, drag: 2, shape: Shape.Spark, stretch: 0.03, fade: 1 }); break;
    }
  }

  /* ---------------- deaths & souls ---------------- */

  /**
   * A kill keeps its element: burned bodies flare and smoke, frozen ones shatter,
   * shocked ones crackle, poisoned ones melt. `at` is chest height, `scale` the body size.
   * Small kills stay cheap (~10–20 particles); bosses scale up.
   */
  death(kind: Kind, at: THREE.Vector3, scale: number, boss: boolean) {
    const m = boss ? 2.6 : 1, h = 0.85 * scale;
    switch (kind) {
      case 'r':
        // the body burns from inside while it topples: tongues over ~0.5 s, then soot
        for (let i = 0, n = this.count(Math.round(5 * m)); i < n; i++)
          this.fire(at.x + rnd(-0.15, 0.15) * scale, at.y - h * rnd(0.1, 0.7), at.z + rnd(-0.15, 0.15) * scale, rnd(-0.2, 0.2), rnd(0.6, 1.2), rnd(-0.2, 0.2),
            0.12 * scale, rnd(0.28, 0.4) * scale * (boss ? 1.4 : 1), rnd(0.4, 0.6), FIRE[1].tint, 1.1, 0.55, i * 0.07);
        this.smoke(at.x, at.y, at.z, 0.05, 0.45, 0, 0.25 * scale, 0.7 * scale * m, rnd(1.2, 1.6), 0x2a2420, 0.45, 0.25);
        break;
      case 'b':
        // frozen solid, then it snaps: heavy shards and a breath of cold
        for (let i = 0, n = this.count(Math.round(9 * m)); i < n; i++) {
          const a = Math.random() * TAU, sp = rnd(0.8, 2.2) * (boss ? 1.4 : 1);
          this.p.emit({ x: at.x, y: at.y - h * rnd(0, 0.5), z: at.z, vx: Math.cos(a) * sp, vy: rnd(0.6, 2), vz: Math.sin(a) * sp,
            color: _c.set(i % 3 ? 0x9ad8ff : 0xe4f6ff), intensity: 2, life: rnd(0.45, 0.7), size: rnd(0.05, 0.1) * scale * 1.6, sizeEnd: 0.015,
            gravity: 9, drag: 1.2, shape: Shape.Shard, stretch: 0.03, fade: 0.8 });
        }
        this.smoke(at.x, at.y - h * 0.4, at.z, 0, 0.15, 0, 0.3 * scale, 0.8 * scale * m, 1, 0xc4dcef, 0.25);
        this.p.emit({ x: at.x, y: 0.035, z: at.z, color: _c.set(0x74c3ff), intensity: 1.2, life: 0.8, size: 0.3 * scale, sizeEnd: 0.9 * scale * m, shape: Shape.Frost, fade: 1.2 });
        break;
      case 'y':
        // crackling: arcs keep jumping across the falling body for a moment
        for (let k = 0; k < (boss ? 8 : 4); k++) {
          const ax = at.x + rnd(-0.2, 0.2) * scale, ay = at.y + rnd(-0.5, 0.25) * h, az = at.z + rnd(-0.2, 0.2) * scale;
          this.arc(_u.set(ax, ay, az), _s.set(at.x + rnd(-0.25, 0.25) * scale, at.y + rnd(-0.6, 0.3) * h, at.z + rnd(-0.25, 0.25) * scale), KIND_COLOR.y, 0.03 * (boss ? 1.6 : 1), 2.8, 0.1, k * 0.09);
        }
        this.p.emit({ x: at.x, y: 0.035, z: at.z, color: _c.set(0xffcd56), intensity: 1.4, life: 0.5, size: 0.3 * scale, sizeEnd: 0.8 * scale * m, shape: Shape.Sun, fade: 1.4 });
        this.smoke(at.x, at.y, at.z, 0, 0.35, 0, 0.18 * scale, 0.5 * scale * m, 0.9, 0x3a3428, 0.3, 0.1);
        break;
      case 'g':
        // melting: drops run off the body and pool under it, a sickly haze lifts
        for (let i = 0, n = this.count(Math.round(6 * m)); i < n; i++) {
          const a = Math.random() * TAU;
          this.p.emit({ x: at.x + Math.cos(a) * 0.12 * scale, y: at.y - h * rnd(0, 0.4), z: at.z + Math.sin(a) * 0.12 * scale,
            vx: Math.cos(a) * rnd(0.2, 0.6), vy: rnd(-0.2, 0.6), vz: Math.sin(a) * rnd(0.2, 0.6), color: _c.set(0x54c845), intensity: 1.1,
            life: rnd(0.4, 0.6), size: rnd(0.04, 0.06) * scale * 1.5, sizeEnd: 0.02, gravity: 7, shape: Shape.Drop, stretch: 0.06, fade: 1, body: 0.9, delay: i * 0.04 });
        }
        this.p.emit({ x: at.x, y: 0.045, z: at.z, color: _c.set(0x2f9a2c), intensity: 1, life: rnd(1.6, 2.2), size: 0.35 * scale * m, shape: Shape.Splat, fade: 1, body: 0.8, delay: 0.2 });
        this.smoke(at.x, at.y - h * 0.3, at.z, 0, 0.3, 0, 0.25 * scale, 0.75 * scale * m, 1.3, 0x5f9a45, 0.22, 0.15);
        break;
      case 'v':
        for (let i = 0, n = this.count(Math.round(6 * m)); i < n; i++) {
          const a = i / n * TAU;
          this.p.emit({ x: at.x, y: at.y - h * 0.2, z: at.z, vx: Math.cos(a) * 0.8, vy: rnd(0.2, 0.8), vz: Math.sin(a) * 0.8,
            color: _c.set(KIND_COLOR.v), intensity: 1.4, life: 0.3, size: 0.055, sizeEnd: 0.02, gravity: 3, shape: Shape.Drop, stretch: 0.045, body: 0.7, fade: 1 });
        }
        break;
      default: {
        // unravelled by arcane force: violet motes spiral off the body as it falls (obsidian's and the storm's in theirs)
        const col = kind === 'k' || kind === 's' ? KIND_COLOR[kind] : KIND_COLOR.a, pale = kind === 'k' ? 0xffd0a0 : kind === 's' ? 0xe0fcff : 0xe8d8ff;
        for (let i = 0, n = this.count(Math.round(8 * m)); i < n; i++) {
          const a = i / n * TAU;
          this.p.emit({ x: at.x + Math.cos(a) * 0.15 * scale, y: at.y - h * rnd(0, 0.6), z: at.z + Math.sin(a) * 0.15 * scale,
            vx: -Math.sin(a) * 0.8, vy: rnd(0.5, 1.2), vz: Math.cos(a) * 0.8, color: _c.set(i % 2 ? col : pale), intensity: 2.2,
            life: rnd(0.5, 0.8), size: 0.035, sizeEnd: 0, drag: 1.5, shape: Shape.Spark, stretch: 0.03, fade: 1, delay: i * 0.02 });
        }
        this.p.emit({ x: at.x, y: 0.035, z: at.z, color: _c.set(col), intensity: 1.2, life: 0.5, size: 0.3 * scale, sizeEnd: 0.8 * scale * m, shape: Shape.Ring, fade: 1.4 });
        break;
      }
    }
  }

  /** The soul leaving the body: a bright wisp gathering before it flies. */
  soulRelease(at: THREE.Vector3, size = 1, blood = false) {
    // a cold spectral wisp tears free of the body: souls are rewards, so they never share the attacks' warm palette
    this.p.emit({ x: at.x, y: at.y, z: at.z, vy: 0.6, color: _c.set(blood ? 0xff7498 : 0x8ff5e6), intensity: 2.2, life: 0.35, size: 0.06 * size, sizeEnd: 0.32 * size, shape: Shape.Glow, fade: 1.4 });
    for (let i = 0, n = this.count(4); i < n; i++) {
      const a = Math.random() * TAU, r = 0.3 * size;
      this.p.emit({ x: at.x + Math.cos(a) * r, y: at.y + rnd(-0.1, 0.2), z: at.z + Math.sin(a) * r, vx: -Math.cos(a) * r / 0.25, vy: 0.4, vz: -Math.sin(a) * r / 0.25,
        color: _c.set(blood ? KIND_COLOR.v : 0x6fe8d8), intensity: 2, life: 0.28, size: 0.025, sizeEnd: 0.01, shape: Shape.Spark, stretch: 0.03, fade: 1 });
    }
  }

  /** A soul in flight (call at ~60 Hz): a ghostly cold-flame tail, a pale head, the odd mote shed. */
  soulTrail(pos: THREE.Vector3, vel: THREE.Vector3, size = 1, head = true, blood = false) {
    if (blood) {
      this.p.emit({ x: pos.x, y: pos.y, z: pos.z, vx: -vel.x * 0.08, vy: -vel.y * 0.08, vz: -vel.z * 0.08,
        color: _c.set(KIND_COLOR.v), intensity: 1.6, life: 0.24, size: 0.085 * size, sizeEnd: 0.012, shape: Shape.Drop, stretch: 0.012, body: 0.5, fade: 0.7 });
      if (head) this.p.emit({ x: pos.x, y: pos.y, z: pos.z, color: _c.set(0xffb4c8), intensity: 2, life: 0.05, size: 0.07 * size, sizeEnd: 0.04, shape: Shape.Glow, fade: 1 });
      return;
    }
    // tail puffs drift back along the path and fade; the cold tint keeps the fire ramp in its own hue
    this.fire(pos.x, pos.y, pos.z, -vel.x * 0.15 + rnd(-0.1, 0.1), -vel.y * 0.15 + 0.2, -vel.z * 0.15 + rnd(-0.1, 0.1),
      0.09 * size, 0.03 * size, rnd(0.22, 0.3), 0x22c4b4, 0.8, 0);
    if (!head) return;
    this.p.emit({ x: pos.x, y: pos.y, z: pos.z, color: _c.set(0x9ffff0), intensity: 2.2, life: 0.05, size: 0.07 * size, sizeEnd: 0.06 * size, shape: Shape.Glow, fade: 1 });
    if (Math.random() < 0.25) this.p.emit({ x: pos.x, y: pos.y, z: pos.z, vx: rnd(-0.3, 0.3), vy: rnd(-0.1, 0.3), vz: rnd(-0.3, 0.3),
      color: _c.set(0x8ff5e6), intensity: 1.8, life: 0.5, size: 0.025, sizeEnd: 0, drag: 2, shape: Shape.Glow, fade: 1 });
  }

  /** The seal swallows a soul: motes spiral into the core, a closing ring and a warm spurt as it turns to charge. */
  soulArrive(seal: THREE.Vector3, size = 1, blood = false) {
    this.p.emit({ x: seal.x, y: seal.y - 0.25, z: seal.z, color: _c.set(blood ? KIND_COLOR.v : 0x6fe8d8), intensity: 1.6, life: 0.25, size: 0.9 * size, sizeEnd: 0.15, shape: Shape.Ring, fade: 0.6 });
    this.p.emit({ x: seal.x, y: seal.y, z: seal.z, color: _c.set(0xffe0a8), intensity: 2.2, life: 0.14, size: 0.15 * size, sizeEnd: 0.45 * size, shape: Shape.Glow, fade: 1.4 });
    for (let i = 0, n = this.count(Math.round(6 * size)); i < n; i++) {
      // a vortex: each mote starts on a ring and is flung tangentially inward
      const a = i / n * TAU, r = 0.55 * size, life = 0.3;
      this.p.emit({ x: seal.x + Math.cos(a) * r, y: seal.y + 0.1, z: seal.z + Math.sin(a) * r,
        vx: (-Math.cos(a) - Math.sin(a) * 1.4) * r / life, vy: -0.2, vz: (-Math.sin(a) + Math.cos(a) * 1.4) * r / life,
        color: _c.set(blood ? 0xff7498 : 0x8ff5e6), intensity: 2.2, life, size: 0.03, sizeEnd: 0.008, drag: 2.5, shape: Shape.Spark, stretch: 0.04, fade: 1 });
    }
    for (let i = 0, n = this.count(Math.round(3 * size)); i < n; i++)
      this.fire(seal.x + rnd(-0.08, 0.08), seal.y, seal.z + rnd(-0.08, 0.08), rnd(-0.2, 0.2), rnd(0.9, 1.5), rnd(-0.2, 0.2), 0.08, 0.2 * size, rnd(0.3, 0.45), 0xffa040, 1.1, 0, 0.12 + i * 0.03);
  }

  /* ---------------- bosses ---------------- */

  /**
   * Presence (call ~8 Hz per visible boss): hellfire licking off the shoulders, dark
   * smoke pooling around the feet, embers from the furnace chest. `feet` is ground level.
   */
  bossAura(feet: THREE.Vector3, scale: number, color: number, pit: boolean, reveal: number) {
    if (reveal < 0.3) return;
    const h = (pit ? 1.25 : 1.05) * scale;
    for (let s = -1; s <= 1; s += 2) if (Math.random() < 0.8) {
      this.fire(feet.x + s * 0.3 * scale + rnd(-0.06, 0.06), feet.y + h, feet.z + rnd(-0.08, 0.08), rnd(-0.1, 0.1), rnd(0.5, 0.9), rnd(-0.1, 0.1),
        0.1 * scale, rnd(0.2, 0.3) * scale, rnd(0.35, 0.5), color, 1.0 * reveal, 0.4);
    }
    if (Math.random() < 0.7) {
      const a = Math.random() * TAU, r = rnd(0.2, 0.45) * scale;
      this.smoke(feet.x + Math.cos(a) * r, 0.08, feet.z + Math.sin(a) * r, Math.cos(a) * 0.15, 0.08, Math.sin(a) * 0.15,
        0.25 * scale, 0.6 * scale, rnd(1, 1.4), pit ? 0x1e1426 : 0x24120e, 0.4 * reveal);
    }
    if (pit && Math.random() < 0.5)
      this.p.emit({ x: feet.x + rnd(-0.1, 0.1), y: feet.y + 0.95 * scale, z: feet.z + 0.25 * scale, vx: rnd(-0.3, 0.3), vy: rnd(0.4, 0.9), vz: rnd(0.1, 0.4),
        color: _c.set(0xd8a8ff), intensity: 2.4 * reveal, life: rnd(0.5, 0.8), size: 0.03, sizeEnd: 0, gravity: -0.3, drag: 1.2, shape: Shape.Glow, fade: 1 });
  }

  /** A heavy foot lands: a glowing crack that cools over seconds. */
  bossStep(at: THREE.Vector3, scale: number, color: number) {
    this.p.emit({ x: at.x, y: 0.03, z: at.z, color: _c.set(color), intensity: 1.1, life: 2.4, size: 0.42 * scale, sizeEnd: 0.46 * scale, shape: Shape.Burn, fade: 1.6 });
  }

  /** The boss steps out of the fog: a roar of fire, a ground shockwave and a smoke burst. */
  bossEnter(feet: THREE.Vector3, scale: number, color: number) {
    this.p.emit({ x: feet.x, y: 0.04, z: feet.z, color: _c.set(color), intensity: 2.2, life: 0.6, size: 0.3 * scale, sizeEnd: 2.6 * scale, shape: Shape.Ring, fade: 1.4 });
    this.p.emit({ x: feet.x, y: 0.04, z: feet.z, color: _c.set(color), intensity: 1.4, life: 0.5, size: 0.6 * scale, sizeEnd: 1.4 * scale, shape: Shape.Floor, fade: 1.6 });
    for (let i = 0, n = this.count(10); i < n; i++) {
      const a = i / n * TAU;
      this.fire(feet.x + Math.cos(a) * 0.35 * scale, 0.1, feet.z + Math.sin(a) * 0.35 * scale, Math.cos(a) * 0.8, rnd(1.4, 2.4), Math.sin(a) * 0.8,
        0.14 * scale, 0.4 * scale, rnd(0.5, 0.7), color, 1.2, 0.5, i * 0.01);
    }
    for (let i = 0, n = this.count(4); i < n; i++)
      this.smoke(feet.x + rnd(-0.3, 0.3), 0.3, feet.z + rnd(-0.3, 0.3), rnd(-0.4, 0.4), rnd(0.4, 0.8), rnd(-0.4, 0.4), 0.4 * scale, 1.2 * scale, rnd(1.4, 1.9), 0x1c1416, 0.5, i * 0.05);
  }

  /** Health threshold crossed: armour cracks off and the core flares. */
  bossStagger(at: THREE.Vector3, scale: number, color: number) {
    for (let i = 0, n = this.count(12); i < n; i++) {
      const a = Math.random() * TAU, sp = rnd(1.2, 2.6);
      this.p.emit({ x: at.x, y: at.y, z: at.z, vx: Math.cos(a) * sp, vy: rnd(1, 2.5), vz: Math.sin(a) * sp, color: _c.set(i % 3 ? 0x6a5a52 : color),
        intensity: i % 3 ? 0.9 : 2.4, life: rnd(0.5, 0.8), size: rnd(0.06, 0.11) * scale, sizeEnd: 0.02, gravity: 10, drag: 0.8, shape: Shape.Shard, stretch: 0.03, fade: 0.7 });
    }
    this.p.emit({ x: at.x, y: at.y, z: at.z, color: _c.set(color), intensity: 2.6, life: 0.2, size: 0.3 * scale, sizeEnd: 1.2 * scale, shape: Shape.Glow, fade: 1.4 });
    this.p.emit({ x: at.x, y: 0.04, z: at.z, color: _c.set(color), intensity: 1.6, life: 0.4, size: 0.4 * scale, sizeEnd: 1.6 * scale, shape: Shape.Ring, fade: 1.4 });
  }

  /**
   * A boss falls in stages without timers (particle delays): ruptures race across the body,
   * a fire/soul-fire column erupts, a shockwave and debris spread, soot climbs, the ground scorches.
   */
  bossDeath(at: THREE.Vector3, feet: THREE.Vector3, scale: number, color: number, pit: boolean) {
    const h = 0.9 * scale;
    // 1) ruptures: bright bursts popping across the body over ~0.45 s
    for (let k = 0; k < 5; k++) {
      const x = at.x + rnd(-0.25, 0.25) * scale, y = at.y + rnd(-0.5, 0.45) * h, z = at.z + rnd(-0.2, 0.2) * scale, d = 0.05 + k * 0.09;
      this.p.emit({ x, y, z, color: _c.set(0xffe2b8), intensity: 3, life: 0.1, size: 0.08 * scale, sizeEnd: 0.45 * scale, shape: Shape.Flare, fade: 1.2, delay: d });
      for (let i = 0; i < 3; i++) this.fire(x, y, z, rnd(-0.8, 0.8), rnd(0.4, 1.4), rnd(-0.8, 0.8), 0.12 * scale, 0.32 * scale, rnd(0.35, 0.5), color, 1.4, 0.5, d);
    }
    // 2) the column: the boss's own fire (violet soul-fire for the pit lord) erupting skyward
    for (let i = 0, n = this.count(14); i < n; i++)
      this.fire(feet.x + rnd(-0.15, 0.15) * scale, 0.15, feet.z + rnd(-0.15, 0.15) * scale, rnd(-0.3, 0.3), rnd(3, 5.2), rnd(-0.3, 0.3),
        0.25 * scale, rnd(0.5, 0.8) * scale, rnd(0.6, 0.9), i % 4 ? color : pit ? 0xe8c8ff : 0xffb050, 1.15, 0.45, 0.45 + i * 0.012);
    // 3) shockwave, ground blaze and debris thrown wide
    this.p.emit({ x: feet.x, y: 0.04, z: feet.z, color: _c.set(color), intensity: 2.4, life: 0.7, size: 0.4 * scale, sizeEnd: 4 * scale, shape: Shape.Ring, fade: 1.3, delay: 0.45 });
    this.p.emit({ x: feet.x, y: 0.04, z: feet.z, color: _c.set(0xfff0d0), intensity: 1.2, life: 0.5, size: 0.3 * scale, sizeEnd: 2.4 * scale, shape: Shape.Ring, fade: 1.5, delay: 0.5 });
    this.p.emit({ x: feet.x, y: 0.035, z: feet.z, color: _c.set(color), intensity: 1.2, life: 3, size: 0.9 * scale, sizeEnd: 1 * scale, shape: Shape.Burn, fade: 1.8, delay: 0.45 });
    for (let i = 0, n = this.count(16); i < n; i++) {
      const a = Math.random() * TAU, sp = rnd(1.5, 3.5);
      this.p.emit({ x: feet.x, y: 0.5 * scale, z: feet.z, vx: Math.cos(a) * sp, vy: rnd(2, 4.5), vz: Math.sin(a) * sp, color: _c.set(i % 2 ? 0x5a4a44 : color),
        intensity: i % 2 ? 0.8 : 2.2, life: rnd(0.7, 1.1), size: rnd(0.07, 0.13) * scale, sizeEnd: 0.03, gravity: 10, drag: 0.5, shape: Shape.Shard, stretch: 0.03, fade: 0.6, delay: 0.45 });
    }
    // 4) soot climbing out of the ruin
    for (let i = 0, n = this.count(6); i < n; i++)
      this.smoke(feet.x + rnd(-0.4, 0.4), 0.3 + i * 0.2, feet.z + rnd(-0.4, 0.4), rnd(-0.2, 0.2), rnd(0.7, 1.3), rnd(-0.2, 0.2),
        0.5 * scale, rnd(1.4, 2) * scale, rnd(2, 2.8), pit ? 0x1a1222 : 0x221a16, 0.6, 0.5 + i * 0.06);
  }

  /** A boss weapon slammed into the road: crack, shockwave, sparks, dust and a lick of its fire. */
  bossSlam(at: THREE.Vector3, scale: number, color: number) {
    this.p.emit({ x: at.x, y: 0.03, z: at.z, color: _c.set(color), intensity: 1.4, life: 3, size: 0.55 * scale, sizeEnd: 0.62 * scale, shape: Shape.Burn, fade: 1.6 });
    this.p.emit({ x: at.x, y: 0.04, z: at.z, color: _c.set(color), intensity: 2, life: 0.45, size: 0.3 * scale, sizeEnd: 1.9 * scale, shape: Shape.Ring, fade: 1.4 });
    this.p.emit({ x: at.x, y: 0.04, z: at.z, color: _c.set(0xfff0d0), intensity: 1.2, life: 0.12, size: 0.3 * scale, sizeEnd: 0.8 * scale, shape: Shape.Floor, fade: 1.2 });
    for (let i = 0, n = this.count(10); i < n; i++) {
      const a = Math.random() * TAU, sp = rnd(1.5, 3.2);
      this.p.emit({ x: at.x, y: 0.1, z: at.z, vx: Math.cos(a) * sp, vy: rnd(1.2, 2.8), vz: Math.sin(a) * sp, color: _c.set(i % 2 ? 0xffc080 : color),
        intensity: 2.4, life: rnd(0.3, 0.5), size: 0.03, sizeEnd: 0, gravity: 7, drag: 1.5, shape: Shape.Spark, stretch: 0.04, fade: 1 });
    }
    for (let i = 0, n = this.count(6); i < n; i++) {
      const a = i / 6 * TAU;
      this.fire(at.x + Math.cos(a) * 0.2 * scale, 0.08, at.z + Math.sin(a) * 0.2 * scale, Math.cos(a) * 0.9, rnd(0.8, 1.4), Math.sin(a) * 0.9,
        0.12 * scale, 0.32 * scale, rnd(0.35, 0.5), color, 1.1, 0.45);
    }
    for (let i = 0, n = this.count(3); i < n; i++)
      this.smoke(at.x + rnd(-0.2, 0.2), 0.12, at.z + rnd(-0.2, 0.2), rnd(-0.5, 0.5), 0.3, rnd(-0.5, 0.5), 0.3 * scale, 0.9 * scale, 1.2, 0x2a2220, 0.4);
  }

  /* ---------------- frost wave & frozen state ---------------- */

  /** The freezing wave: a racing ring, a frost bloom over the area, ice spikes along its edge, cold mist and snow. */
  frostWave(at: THREE.Vector3, radius: number) {
    this.p.emit({ x: at.x, y: 0.05, z: at.z, color: _c.set(0xbfe6ff), intensity: 2.6, life: 0.4, size: 0.2, sizeEnd: radius / 0.86, shape: Shape.Ring, fade: 0.8 });
    this.p.emit({ x: at.x, y: 0.05, z: at.z, color: _c.set(0xffffff), intensity: 1.4, life: 0.32, size: 0.1, sizeEnd: radius * 0.8 / 0.86, shape: Shape.Ring, fade: 1, delay: 0.05 });
    this.p.emit({ x: at.x, y: 0.04, z: at.z, color: _c.set(0x74c3ff), intensity: 1.5, life: 1.6, size: radius * 0.4, sizeEnd: radius * 1.15, shape: Shape.Frost, fade: 1 });
    this.p.emit({ x: at.x, y: 0.03, z: at.z, color: _c.set(0x5fb0ff), intensity: 1, life: 0.6, size: radius * 0.6, sizeEnd: radius * 1.1, shape: Shape.Floor, fade: 1.6 });
    for (let i = 0, n = this.count(16); i < n; i++) {
      const a = i / n * TAU + rnd(-0.1, 0.1), r = radius * rnd(0.85, 1);
      this.crystal(at.x + Math.cos(a) * r, at.z + Math.sin(a) * r, rnd(0.1, 0.16), rnd(0.3, 0.6), 1.2, 0.3 + rnd(0, 0.05), at.x, at.z, rnd(0.3, 0.5), 0.4);
    }
    for (let i = 0, n = this.count(6); i < n; i++) {
      const a = Math.random() * TAU, r = Math.sqrt(Math.random()) * radius * 0.7;
      this.smoke(at.x + Math.cos(a) * r, 0.12, at.z + Math.sin(a) * r, Math.cos(a) * 0.5, 0.12, Math.sin(a) * 0.5, 0.35, 1, rnd(1.2, 1.7), 0xc4dcef, 0.22, rnd(0, 0.25));
    }
    for (let i = 0, n = this.count(24); i < n; i++) {
      const a = Math.random() * TAU, r = Math.sqrt(Math.random()) * radius;
      this.p.emit({ x: at.x + Math.cos(a) * r, y: rnd(0.8, 1.6), z: at.z + Math.sin(a) * r, vx: rnd(-0.1, 0.1), vy: 0, vz: rnd(-0.1, 0.1),
        color: _c.set(0xeaf6ff), intensity: 1.8, life: rnd(1.1, 1.6), size: rnd(0.02, 0.035), sizeEnd: 0.01, gravity: 0.6, drag: 1.5, shape: Shape.Glow, fade: 0.8, delay: rnd(0, 0.35) });
    }
  }

  /** The instant an enemy freezes: a crust of glittering shards closes on it. */
  freezeCrust(at: THREE.Vector3, scale: number) {
    for (let i = 0, n = this.count(6); i < n; i++) {
      const a = Math.random() * TAU, r = 0.4 * scale;
      this.p.emit({ x: at.x + Math.cos(a) * r, y: at.y + rnd(-0.4, 0.3) * scale, z: at.z + Math.sin(a) * r, vx: -Math.cos(a) * r / 0.2, vy: 0, vz: -Math.sin(a) * r / 0.2,
        color: _c.set(0xd8f2ff), intensity: 2.2, life: 0.2, size: 0.04, sizeEnd: 0.015, shape: Shape.Shard, stretch: 0.03, fade: 1 });
    }
    this.p.emit({ x: at.x, y: at.y, z: at.z, color: _c.set(0xbfe6ff), intensity: 1.6, life: 0.25, size: 0.3 * scale, sizeEnd: 0.8 * scale, shape: Shape.Glow, fade: 1.4 });
    for (let i = 0; i < 3; i++) {
      const a = i / 3 * TAU + rnd(-0.3, 0.3), r = 0.22 * scale;
      this.crystal(at.x + Math.cos(a) * r, at.z + Math.sin(a) * r, 0.09 * scale, rnd(0.22, 0.34) * scale, 2.4, i * 0.03, at.x, at.z, 0.4, 0.6);
    }
  }

  /** Thaw: the ice crust cracks off and slides away as cold vapour. */
  thaw(at: THREE.Vector3, scale: number) {
    for (let i = 0, n = this.count(8); i < n; i++) {
      const a = Math.random() * TAU, sp = rnd(0.6, 1.4);
      this.p.emit({ x: at.x, y: at.y + rnd(-0.4, 0.3) * scale, z: at.z, vx: Math.cos(a) * sp, vy: rnd(0.4, 1.2), vz: Math.sin(a) * sp,
        color: _c.set(0xbfe6ff), intensity: 1.8, life: rnd(0.4, 0.6), size: rnd(0.04, 0.08) * scale * 1.5, sizeEnd: 0.01, gravity: 9, drag: 1, shape: Shape.Shard, stretch: 0.03, fade: 0.8 });
    }
    this.smoke(at.x, at.y - 0.3 * scale, at.z, 0, 0.2, 0, 0.25 * scale, 0.7 * scale, 0.9, 0xc4dcef, 0.2);
  }
}
